import { useState } from 'react';
import { X, ArrowLeft, Palette, ExternalLink, Sparkles, Users } from 'lucide-react';
import { useApp } from '@context/AppContext';
import { getAllStyles, getWorkshopTradition, applyStylePreset } from '@utils/stylePresets';

/**
 * ArtStyleExplorer — the curriculum of the workshop.
 *
 * Browsable gallery of all art styles with history, key artists,
 * technique tips, and tool presets. Also includes the "Workshop Tradition"
 * section — the historical through-line that frames DrawLab.
 */
export default function ArtStyleExplorer({ onClose }) {
  const { dispatch } = useApp();
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [showTradition, setShowTradition] = useState(false);

  const styles = getAllStyles();
  const tradition = getWorkshopTradition();
  const selectedStyle = selectedStyleId ? styles.find(s => s.id === selectedStyleId) : null;

  const handleApplyPreset = (style) => {
    applyStylePreset(dispatch, style);
    dispatch({
      type: 'ADD_TOAST',
      payload: { message: `${style.name} preset applied — try drawing now!` },
    });
    onClose();
  };

  // Individual style detail view
  if (selectedStyle) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
            <button
              onClick={() => setSelectedStyleId(null)}
              className="flex items-center gap-2 text-slate-400 hover:text-charcoal transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-display">All styles</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Coloured hero */}
            <div
              className="p-6 text-white"
              style={{ backgroundColor: selectedStyle.colour }}
            >
              <p className="text-xs font-mono uppercase tracking-wider opacity-80 mb-1">
                {selectedStyle.period}
              </p>
              <h2 className="text-3xl font-heading font-bold mb-2 flex items-center gap-3">
                <span>{selectedStyle.emoji}</span>
                <span>{selectedStyle.name}</span>
              </h2>
              <p className="text-base italic opacity-90">{selectedStyle.tagline}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary */}
              <div>
                <p className="text-sm text-charcoal font-display leading-relaxed">
                  {selectedStyle.summary}
                </p>
              </div>

              {/* History */}
              <div>
                <h3 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
                  The Story
                </h3>
                <p className="text-sm text-charcoal-muted font-display leading-relaxed">
                  {selectedStyle.history}
                </p>
              </div>

              {/* Key artists */}
              <div>
                <h3 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-3">
                  Key Artists
                </h3>
                <div className="space-y-2">
                  {selectedStyle.keyArtists.map((artist) => (
                    <div key={artist.name} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-amber shrink-0 mt-2" />
                      <div className="text-sm">
                        <span className="font-semibold text-charcoal font-display">
                          {artist.name}
                        </span>
                        <span className="text-slate-400 font-display ml-1.5">
                          — {artist.note}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Characteristics */}
              <div>
                <h3 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-3">
                  What Makes It Recognisable
                </h3>
                <ul className="space-y-1.5">
                  {selectedStyle.characteristics.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm text-charcoal-muted font-display">
                      <span className="text-amber shrink-0">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technique tips */}
              <div className="bg-amber/5 border border-amber/20 rounded-xl p-4">
                <h3 className="text-xs font-mono text-amber uppercase tracking-wider mb-3">
                  Try This
                </h3>
                <ul className="space-y-2">
                  {selectedStyle.techniqueTips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-charcoal font-display">
                      <span className="text-amber shrink-0 font-bold">→</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Workshop connection */}
              {selectedStyle.workshopConnection && (
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-start gap-3">
                  <Users className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-1">
                      The Workshop Connection
                    </p>
                    <p className="text-sm text-charcoal-muted font-display leading-relaxed">
                      {selectedStyle.workshopConnection}
                    </p>
                  </div>
                </div>
              )}

              {/* Dig deeper links */}
              <div>
                <h3 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-3">
                  Dig Deeper
                </h3>
                <div className="space-y-1.5">
                  {selectedStyle.digDeeper.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 font-display group"
                    >
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="underline underline-offset-2">{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer action */}
          <div className="px-5 py-4 border-t border-slate-100 shrink-0 flex gap-3">
            <button
              onClick={() => handleApplyPreset(selectedStyle)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Apply {selectedStyle.name} Preset
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Workshop Tradition detail view
  if (showTradition) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
            <button
              onClick={() => setShowTradition(false)}
              className="flex items-center gap-2 text-slate-400 hover:text-charcoal transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-display">All styles</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 bg-charcoal text-canvas">
              <p className="text-xs font-mono text-amber-400 uppercase tracking-[0.2em] mb-2">
                The continuity of craft
              </p>
              <h2 className="text-3xl font-heading font-bold mb-3">
                {tradition.title}
              </h2>
              <p className="text-sm text-canvas/70 font-display leading-relaxed">
                {tradition.description}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {tradition.examples.map((ex) => (
                <div key={ex.name} className="flex gap-4">
                  <div className="shrink-0 w-24 text-right">
                    <p className="text-xs font-mono text-amber font-bold">{ex.place}</p>
                  </div>
                  <div className="flex-1 pb-3 border-l-2 border-slate-100 pl-4 -ml-1 relative">
                    <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-amber" />
                    <h3 className="text-sm font-heading font-bold text-charcoal mb-1">
                      {ex.name}
                    </h3>
                    <p className="text-xs text-charcoal-muted font-display leading-relaxed">
                      {ex.summary}
                    </p>
                  </div>
                </div>
              ))}

              {tradition.acknowledgement && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-[11px] font-mono text-slate-400 uppercase tracking-wider mb-2">
                    Acknowledgement
                  </p>
                  <p className="text-xs text-charcoal-muted font-display leading-relaxed italic">
                    {tradition.acknowledgement}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main grid view — all styles
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-charcoal font-display">Art Styles</h2>
            <p className="text-xs text-slate-400 font-display">
              Six traditions from the workshop lineage
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Styles grid */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {styles.map((style) => (
              <button
                key={style.id}
                onClick={() => setSelectedStyleId(style.id)}
                className="text-left rounded-xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all group"
              >
                <div
                  className="p-4 text-white"
                  style={{ backgroundColor: style.colour }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xl">{style.emoji}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider opacity-80">
                      {style.period}
                    </span>
                  </div>
                  <h3 className="text-lg font-heading font-bold mb-0.5">
                    {style.name}
                  </h3>
                  <p className="text-xs italic opacity-90">{style.tagline}</p>
                </div>
                <div className="p-3 bg-white">
                  <p className="text-xs text-slate-500 font-display leading-relaxed line-clamp-3">
                    {style.summary}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Workshop Tradition CTA */}
          <button
            onClick={() => setShowTradition(true)}
            className="w-full mt-5 p-4 rounded-xl bg-charcoal text-canvas hover:bg-charcoal-light transition-colors flex items-center gap-4 group text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-amber/20 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-bold text-canvas mb-0.5">
                The Workshop Tradition
              </p>
              <p className="text-xs text-canvas/60 font-display leading-relaxed">
                The 600-year lineage behind DrawLab — from Verrocchio's bottega to today
              </p>
            </div>
            <ArrowLeft className="w-4 h-4 rotate-180 text-canvas/40 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
          </button>
        </div>
      </div>
    </div>
  );
}
